import { IAuthAccessToken } from './authentication.interface';

export enum DeviceLoginStatus {
  Pending = 0,
  Approved = 1,
  Expired = 2,
  Cancelled = 3,
  Denied = 4,
}

export interface DeviceLoginStartRequest {
  deviceId: string;
  deviceName?: string;
  appSource?: string;
  origin?: string;
}

export interface DeviceLoginStartResponse {
  attemptId: string;
  deviceCode: string;
  activationCode: string;
  verificationUri: string;
  verificationUriComplete: string;
  expiresIn: string;
  pollingIntervalInSeconds: number;
  status: DeviceLoginStatus;
}

/**
 * SignalR token payload uses camelCase; `IAuthAccessToken` (the shape consumed
 * by `AuthStore.setAuthData` and the token interceptor) uses PascalCase. We
 * receive the camelCase shape from the hub and normalize it before persisting.
 */
export interface DeviceLoginHubToken {
  token: string;
  type: 'Bearer';
  tokenId: string;
  personId: string;
  supportPersonId: string | null;
  tenantId: string;
  sessionId: string | null;
  addedOn: string | null;
}

export interface DeviceLoginApprovedEvent {
  status: DeviceLoginStatus;
  expiresIn: string;
  pollingIntervalInSeconds: number;
  token: DeviceLoginHubToken;
}

export function toAuthAccessToken(
  hubToken: DeviceLoginHubToken,
): IAuthAccessToken {
  return {
    Token: hubToken.token,
    Type: 'Bearer',
    TokenId: hubToken.tokenId,
    PersonId: hubToken.personId,
    TenantId: hubToken.tenantId,
    TenantSupportId: hubToken.supportPersonId ?? '',
  };
}

/**
 * Backend serialization may be either camelCase OR PascalCase depending on env
 * (per integration guideline). Promote PascalCase top-level keys to their
 * camelCase form without overwriting existing camelCase keys, so consumers can
 * read a single canonical shape.
 */
export function normalizeToCamelCase<T extends object>(
  payload: Record<string, unknown>,
): T {
  const result: Record<string, unknown> = { ...payload };
  for (const [key, value] of Object.entries(payload)) {
    if (!key) continue;
    const first = key.charAt(0);
    if (first === first.toUpperCase() && first !== first.toLowerCase()) {
      const camelKey = first.toLowerCase() + key.slice(1);
      if (result[camelKey] === undefined) {
        result[camelKey] = value;
      }
    }
  }
  return result as T;
}
